import { Spinner } from '@consigli/facade';
import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DocumentPaneRenderProps } from '@aecom-ai-engineering/react-components';

interface JsonRendererPaneProps {
  active: boolean;
  src: DocumentPaneRenderProps['src'];
}

const JsonRendererPane: FC<JsonRendererPaneProps> = ({ active, src }) => {
  const { t } = useTranslation();
  const [jsonData, setJsonData] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fileUrl = typeof src === 'string' ? src : src instanceof URL ? src.href : undefined;

  useEffect(() => {
    if (!active || !fileUrl) return;

    const controller = new AbortController();
    const fetchJson = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(fileUrl, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Failed to fetch JSON: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        setJsonData(data);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(
          err instanceof Error
            ? err.message
            : t('document-viewer.json-load-failed', 'Failed to load JSON'),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchJson();
    return () => controller.abort();
  }, [active, fileUrl, t]);

  if (!active) return null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="text-red-500">
          {t('document-viewer.json-error', 'Error loading JSON')}: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto bg-gray-50 p-4">
      <pre className="bg-white rounded-lg p-4 shadow-sm text-sm font-mono overflow-auto">
        {JSON.stringify(jsonData, null, 2)}
      </pre>
    </div>
  );
};

export function createJsonRenderer() {
  return function JsonRenderer(props: DocumentPaneRenderProps) {
    return <JsonRendererPane active={props.active} src={props.src} />;
  };
}
